import React, { useState } from 'react';
import Navigation from '@/components/shared/Navigation';
import Footer from '@/components/shared/Footer';
import { CheckCircle, AlertCircle } from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    role: '',
    engagementType: '',
    assetType: '',
    scope: '',
    timeline: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Simple validation
    if (!formData.name || !formData.email || !formData.company) {
      setError('Please fill in all required fields');
      return;
    }

    // In a real application, this would send to a backend
    console.log('Form submitted:', formData);
    setSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        name: '',
        company: '',
        email: '',
        role: '',
        engagementType: '',
        assetType: '',
        scope: '',
        timeline: ''
      });
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] font-sans">
      <Navigation />

      {/* Header */}
      <section className="bg-white py-16 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-4xl font-semibold text-[#0A2540] mb-4">
            Request a Confidential Consultation
          </h1>
          <p className="text-xl text-[#2E2E2E] max-w-3xl">
            Tell us about your analysis requirements and we'll schedule a consultation to discuss how we can support your team.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          {submitted ? (
            <div className="bg-white p-12 rounded-lg border border-gray-200 text-center">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
              <h2 className="text-2xl font-semibold text-[#0A2540] mb-4">
                Thank You
              </h2>
              <p className="text-lg text-[#2E2E2E]">
                We've received your request and will be in touch within 24 hours to schedule a consultation.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-lg border border-gray-200">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold text-[#0A2540] mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#4A6FA5]"
                    placeholder="Your full name"
                  />
                </div>

                {/* Company */}
                <div>
                  <label className="block text-sm font-semibold text-[#0A2540] mb-2">
                    Company *
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#4A6FA5]"
                    placeholder="Company name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-[#0A2540] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#4A6FA5]"
                    placeholder="your.email@company.com"
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-semibold text-[#0A2540] mb-2">
                    Role
                  </label>
                  <input
                    type="text"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#4A6FA5]"
                    placeholder="e.g., Director of Acquisitions"
                  />
                </div>

                {/* Type of Engagement */}
                <div>
                  <label className="block text-sm font-semibold text-[#0A2540] mb-2">
                    Type of Engagement
                  </label>
                  <select
                    name="engagementType"
                    value={formData.engagementType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#4A6FA5]"
                  >
                    <option value="">Select type</option>
                    <option value="dedicated-analyst">Dedicated Analyst Pod</option>
                    <option value="underwriting">Underwriting Support</option>
                    <option value="lease-abstraction">Lease Abstraction</option>
                    <option value="argus">ARGUS Support</option>
                    <option value="market-research">Market Research</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Asset Type */}
                <div>
                  <label className="block text-sm font-semibold text-[#0A2540] mb-2">
                    Asset Type
                  </label>
                  <select
                    name="assetType"
                    value={formData.assetType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#4A6FA5]"
                  >
                    <option value="">Select asset type</option>
                    <option value="industrial">Industrial & Logistics</option>
                    <option value="office">Office</option>
                    <option value="retail">Retail</option>
                    <option value="multifamily">Multifamily</option>
                    <option value="mixed-use">Mixed-Use</option>
                    <option value="multiple">Multiple Asset Types</option>
                  </select>
                </div>
              </div>

              {/* Scope Description */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-[#0A2540] mb-2">
                  Scope Description
                </label>
                <textarea
                  name="scope"
                  value={formData.scope}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#4A6FA5]"
                  placeholder="Briefly describe your analysis requirements and volume expectations..."
                />
              </div>

              {/* Expected Timeline */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-[#0A2540] mb-2">
                  Expected Timeline
                </label>
                <input
                  type="text"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded focus:outline-none focus:border-[#4A6FA5]"
                  placeholder="e.g., Immediate need, Q1 2025, Ongoing"
                />
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full px-8 py-4 bg-[#0A2540] text-white font-semibold rounded hover:bg-[#0d3355] transition-colors"
              >
                Request a Confidential Consultation
              </button>

              <p className="text-sm text-gray-500 mt-4 text-center">
                All inquiries are handled confidentially. We typically respond within 24 hours.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Additional Contact Info */}
      <section className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-semibold text-[#0A2540] mb-6">
            Prefer Direct Contact?
          </h2>
          <p className="text-lg text-[#2E2E2E] mb-4">
            Email us directly at: <a href="mailto:contact@pulseshift.com" className="text-[#4A6FA5] hover:underline">contact@pulseshift.com</a>
          </p>
          <p className="text-[#2E2E2E]">
            We'll schedule a call to discuss your requirements and provide a tailored proposal.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
